/**
 * SimulationPage Component
 *
 * Main page for running EnergyPlus simulations.
 * IDF file is automatically loaded from the editor (via FileToolbar in header).
 * User only needs to select weather file (EPW) on this page.
 * Provides simulation control, console output, and results viewing.
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SimulationPanel } from '../simulation/components/SimulationPanel';
import { useSimulationStore } from '../simulation/simulationStore';
import { useIDFStore } from '../stores/idfStore';
import { useFileStore, WeatherFileSelector } from '../storage';
import type { WeatherFileInfo } from '../storage';
import { fetchEpwFile } from '../weather';
import { ErrorPanel } from '../ai';
import { useIsMobile } from '../hooks';
import { ResizablePanel } from './ResizablePanel';

/**
 * Variable list component for viewing available output variables
 */
function VariableList({ showPlaceholder = false }: { showPlaceholder?: boolean }) {
  const availableVariables = useSimulationStore((state) => state.availableVariables);
  const [filter, setFilter] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const filteredVariables = availableVariables.filter(
    (v) =>
      v.variableName.toLowerCase().includes(filter.toLowerCase()) ||
      v.keyValue.toLowerCase().includes(filter.toLowerCase())
  );

  const handleCopyVariable = useCallback((variable: typeof availableVariables[0]) => {
    const text = `Output:Variable,${variable.keyValue},${variable.variableName},${variable.reportFrequency};`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(variable.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  // Show placeholder when no variables and placeholder mode is enabled
  if (availableVariables.length === 0) {
    if (!showPlaceholder) return null;

    return (
      <div className="flex flex-col h-full bg-card rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            Output Variables
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center text-muted-foreground">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">Run a simulation to see available output variables</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-card rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Output Variables
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            ({availableVariables.length})
          </span>
        </h3>
        <input
          type="text"
          value={filter}
          onChange={(e) => { setFilter(e.target.value); }}
          placeholder="Filter variables..."
          className="w-full px-3 py-1.5 bg-secondary border border-border rounded text-sm text-foreground placeholder-muted-foreground"
        />
      </div>
      <div className="flex-1 overflow-auto p-2">
        {filteredVariables.length === 0 ? (
          <div className="text-center text-muted-foreground py-4">
            No variables match your filter
          </div>
        ) : (
          <div className="space-y-1">
            {filteredVariables.map((variable) => (
              <div
                key={variable.id}
                className="group px-3 py-2 bg-secondary/50 rounded text-sm hover:bg-secondary transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-foreground truncate flex-1">
                    {variable.variableName}
                  </div>
                  <button
                    onClick={() => handleCopyVariable(variable)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-muted rounded transition-all"
                    title="Copy as Output:Variable"
                  >
                    {copiedId === variable.id ? (
                      <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{variable.keyValue}</span>
                  <span>•</span>
                  <span>{variable.units || 'no units'}</span>
                  <span>•</span>
                  <span>{variable.reportFrequency}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Placeholder panel for errors before simulation runs
 */
function ErrorPanelPlaceholder() {
  return (
    <div className="flex flex-col h-full bg-card rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Simulation Errors</h3>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center text-muted-foreground">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm">Errors and warnings will appear here after simulation</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Simulation requirements checklist
 */
function RequirementsChecklist({ hasIdf, hasEpw }: { hasIdf: boolean; hasEpw: boolean }) {
  const allMet = hasIdf && hasEpw;

  if (allMet) return null;

  return (
    <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
      <div className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">
        Requirements to run simulation:
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2">
          {hasIdf ? (
            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className={hasIdf ? 'text-muted-foreground' : 'text-foreground'}>
            IDF file loaded
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasEpw ? (
            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className={hasEpw ? 'text-muted-foreground' : 'text-foreground'}>
            Weather file selected
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Main simulation page component
 */
export function SimulationPage() {
  const isMobile = useIsMobile();
  const rawContent = useIDFStore((state) => state.rawContent);
  const currentFileName = useFileStore((state) => state.currentFileName);
  const selectedLibraryWeatherFile = useFileStore((state) => state.selectedLibraryWeatherFile);
  const setInput = useSimulationStore((state) => state.setInput);
  const storedInput = useSimulationStore((state) => state.input);
  const hasResults = useSimulationStore((state) => state.output !== null);
  const status = useSimulationStore((state) => state.status);

  // Initialize EPW file from simulation store if available (for persistence across navigation)
  const [epwFile, setEpwFile] = useState<{ name: string; content: string } | null>(() => {
    if (storedInput?.epw && storedInput?.epwName) {
      return { name: storedInput.epwName, content: storedInput.epw };
    }
    return null;
  });
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState<string | null>(null);

  // Track which library file we've auto-loaded to avoid re-fetching
  const autoLoadedFileIdRef = useRef<string | null>(null);

  // Sync IDF from store and EPW file with simulation store whenever they change
  useEffect(() => {
    if (rawContent) {
      // Build input object - only include epw if it exists
      const idfName = currentFileName || 'current.idf';
      const inputData = epwFile
        ? {
            idf: rawContent,
            idfName: idfName,
            epw: epwFile.content,
            epwName: epwFile.name,
          }
        : {
            idf: rawContent,
            idfName: idfName,
          };
      setInput(inputData);
    } else {
      // Clear input when no IDF is loaded to prevent stale data
      // Type assertion needed as setInput type doesn't reflect that null is accepted
      setInput(null as unknown as Parameters<typeof setInput>[0]);
    }
  }, [rawContent, currentFileName, epwFile, setInput]);

  // Auto-load library weather file if selected from the Weather page
  useEffect(() => {
    // Skip if we've already loaded this file or if there's no library file selected
    if (!selectedLibraryWeatherFile) {
      return;
    }

    // Skip if we've already auto-loaded this specific file
    if (autoLoadedFileIdRef.current === selectedLibraryWeatherFile.id) {
      return;
    }

    // Skip if we already have a weather file loaded with the same name
    if (epwFile?.name === selectedLibraryWeatherFile.name) {
      autoLoadedFileIdRef.current = selectedLibraryWeatherFile.id;
      return;
    }

    // Auto-load the library weather file
    const loadWeatherFile = async () => {
      setWeatherLoading(true);
      setWeatherError(null);

      try {
        const content = await fetchEpwFile(selectedLibraryWeatherFile.url);
        setEpwFile({ name: selectedLibraryWeatherFile.name, content });
        autoLoadedFileIdRef.current = selectedLibraryWeatherFile.id;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load weather file';
        setWeatherError(message);
        console.error('Auto-load weather file error:', err);
      } finally {
        setWeatherLoading(false);
      }
    };

    loadWeatherFile();
  }, [selectedLibraryWeatherFile, epwFile?.name]);

  // Weather file selection handler (for library/bundled files with URLs)
  const handleWeatherFileChange = useCallback(
    async (weatherFile: WeatherFileInfo | null) => {
      if (!weatherFile) {
        // Custom file upload is handled separately
        return;
      }

      setWeatherLoading(true);
      setWeatherError(null);

      try {
        // Use fetchEpwFile which handles both direct EPW and ZIP files
        const content = await fetchEpwFile(weatherFile.url);
        setEpwFile({ name: weatherFile.name, content });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load weather file';
        setWeatherError(message);
        console.error('Weather file fetch error:', err);
      } finally {
        setWeatherLoading(false);
      }
    },
    []
  );

  // Custom EPW file upload handler
  const handleCustomFileSelect = useCallback(
    async (file: File) => {
      setWeatherLoading(true);
      setWeatherError(null);

      try {
        const content = await file.text();
        setEpwFile({ name: file.name, content });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to read weather file';
        setWeatherError(message);
        console.error('Weather file read error:', err);
      } finally {
        setWeatherLoading(false);
      }
    },
    []
  );

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h1 className="text-xl font-bold text-foreground">EnergyPlus Simulation</h1>
      </div>

      {/* Content */}
      <div className={`flex-1 min-h-0 p-4 md:p-6 gap-4 md:gap-6 overflow-auto ${
        isMobile ? 'flex flex-col' : 'flex'
      }`}>
        {/* Left panel - File upload and simulation */}
        <div className={`flex flex-col gap-4 md:gap-6 min-w-0 ${
          isMobile ? 'w-full' : 'flex-1'
        }`}>
          {/* IDF File Status and Weather File Selection */}
          <div className="bg-card rounded-lg p-4">
            <h2 className="text-lg font-semibold text-foreground mb-4">Simulation Setup</h2>
            <div className="space-y-4">
              {/* IDF File Status */}
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded">
                <svg
                  className="w-5 h-5 text-muted-foreground flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-muted-foreground">IDF File:</span>
                  {rawContent ? (
                    <div className="text-sm text-foreground truncate">
                      {currentFileName || 'current.idf'} (loaded from editor)
                    </div>
                  ) : (
                    <div className="text-sm text-red-400 flex items-center gap-2">
                      <span>No IDF file loaded.</span>
                      <Link
                        to="/editor"
                        className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
                      >
                        Open Editor →
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* EPW File Selection */}
              <div className="space-y-2" data-onboarding="weather-selector">
                <WeatherFileSelector
                  onWeatherFileChange={handleWeatherFileChange}
                  onCustomFileSelect={handleCustomFileSelect}
                />
                {weatherLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading weather file...
                  </div>
                )}
                {weatherError && (
                  <div className="text-sm text-red-500">
                    {weatherError}
                  </div>
                )}
                {epwFile && !weatherLoading && (
                  <div className="text-sm text-green-600 dark:text-green-400">
                    Loaded: {epwFile.name}
                  </div>
                )}
              </div>

              {/* Requirements checklist */}
              <RequirementsChecklist hasIdf={!!rawContent} hasEpw={!!epwFile} />
            </div>
          </div>

          {/* Simulation panel */}
          <div className={isMobile ? 'min-h-[300px] max-h-[50vh]' : 'flex-1 min-h-0'}>
            <SimulationPanel />
          </div>
        </div>

        {/* Right panel - Results and Errors (always visible) */}
        {isMobile ? (
          <div className="flex flex-col gap-4 overflow-hidden w-full">
            {/* Results button when simulation complete */}
            {hasResults && status === 'complete' && (
              <Link
                to="/results"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                View Results Dashboard
              </Link>
            )}

            {/* AI-Powered Error Panel or placeholder */}
            {hasResults ? (
              <ErrorPanel
                errorSummary={useSimulationStore.getState().output?.err || null}
                className="max-h-[300px]"
              />
            ) : (
              <div className="max-h-[300px]">
                <ErrorPanelPlaceholder />
              </div>
            )}

            {/* Variable List */}
            <div className="min-h-[200px]">
              <VariableList showPlaceholder={!hasResults} />
            </div>
          </div>
        ) : (
          <ResizablePanel
            side="right"
            defaultWidth={384}
            minWidth={280}
            maxWidth={550}
            storageKey="simulation-right-panel"
            className="flex flex-col gap-4 overflow-hidden"
            hideOnMobile={false}
            resizeAriaLabel="Resize simulation results panel"
          >
            {/* Results button when simulation complete */}
            {hasResults && status === 'complete' && (
              <Link
                to="/results"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                View Results Dashboard
              </Link>
            )}

            {/* AI-Powered Error Panel or placeholder */}
            {hasResults ? (
              <ErrorPanel
                errorSummary={useSimulationStore.getState().output?.err || null}
                className="max-h-[50%] min-h-0"
              />
            ) : (
              <div className="max-h-[50%] min-h-[200px]">
                <ErrorPanelPlaceholder />
              </div>
            )}

            {/* Variable List */}
            <div className="flex-1 min-h-0">
              <VariableList showPlaceholder={!hasResults} />
            </div>
          </ResizablePanel>
        )}
      </div>
    </div>
  );
}

export default SimulationPage;
